import { useAtom, useAtomValue } from 'jotai';
import { useCallback } from 'react';

import {
  inquiryCloseAtom,
  isFeedbackActiveAtom,
  type InquiryCloseState,
  type NavigationTarget,
} from '@/services/globalState';

/** Hook for managing inquiry close state machine. */
export function useInquiryClose() {
  const [state, dispatch] = useAtom(inquiryCloseAtom);

  const isFeedbackActive = useAtomValue(isFeedbackActiveAtom);

  const initiate = useCallback(
    (conversationId: string) => {
      dispatch({ type: 'INITIATE', conversationId });
    },
    [dispatch]
  );

  const confirm = useCallback(() => {
    dispatch({ type: 'CONFIRM' });
  }, [dispatch]);

  const setNavigationTarget = useCallback(
    (target: NavigationTarget) => {
      dispatch({ type: 'SET_NAVIGATION_TARGET', target });
    },
    [dispatch]
  );

  const startFeedback = useCallback(() => {
    dispatch({ type: 'START_FEEDBACK' });
  }, [dispatch]);

  const completeFeedback = useCallback(() => {
    dispatch({ type: 'COMPLETE_FEEDBACK' });
  }, [dispatch]);

  const reset = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, [dispatch]);

  const isActiveFor = useCallback(
    (conversationId: string): boolean => {
      return state.conversationId === conversationId && state.status !== 'idle';
    },
    [state.conversationId, state.status]
  );

  return {
    state,
    status: state.status,
    conversationId: state.conversationId,
    navigationTarget: state.navigationTarget,
    isFeedbackActive,
    initiate,
    confirm,
    setNavigationTarget,
    startFeedback,
    completeFeedback,
    reset,
    isActiveFor,
    dispatch,
  };
}

/**
 * Read-only hook for observing inquiry close state.
 */
export function useInquiryCloseState() {
  const state = useAtomValue(inquiryCloseAtom) as InquiryCloseState;

  const isFeedbackActive = useAtomValue(isFeedbackActiveAtom);

  return {
    state,
    status: state.status,
    conversationId: state.conversationId,
    navigationTarget: state.navigationTarget,
    isFeedbackActive,
  };
}
